import { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { Provider, useDispatch } from "react-redux";
import csvIcon from "../assets/img/csvicon.svg";
import {
  addDownloadToLog,
  addSuccessDownloadToLog,
  completedDownload,
} from "../Layouts/Navbar/DownloadProgress/DownloadLogSlice";
import { isArray, kindOf, showToast } from "../lib/utility";
import { REPORTSERVERTIME } from "../lib/utils";
import { middleware, store } from "../redux/store";
import FileDownloadConfirm from "../components/Common/FileDownloadConfirm";

const confirmModalHandler = ({
  currId,
  data,
  fileType,
  reportName,
  chartsRef,
  mappable,
}) => {
  const container = document.createElement("div");
  container.id = "file-download-confirm-container";
  document.body.appendChild(container);
  const root = createRoot(container);

  root.render(
    <Provider store={store}>
      <FileDownloadConfirm
        {...{
          currId,
          data,
          fileType,
          reportName,
          chartsRef,
          mappable,
        }}
      />
    </Provider>
  );
};

export const usePaginationNDownload = (
  getReportData,
  reportData,
  downloadOptions = {}
) => {
  let {
    getReportThunk,
    reportName,
    filterData,
    searchVal,
    mappable,
    selector,
    chartsRef,
    contentField = "data",
    onPageChange,
    onPerPageChange,
    initialPageSize = 50,
    // Opt-in: only reports that need totalCount/firstTime on download (e.g. Delivery Advance)
    includeTotalCountOnDownload = false,
    downloadFactor: downloadFactorOverride,
  } = downloadOptions;
  let downloadFactor =
    downloadFactorOverride ?? REPORTSERVERTIME[reportName] ?? 0.04;

  const [pagination, setPagination] = useState({
    current: 0,
    size: initialPageSize,
    isLastPage: false,
    totalPages: 0,
    totalItems: 0,
  });
  const [fileType, setFileType] = useState({
    value: "csv",
    label: "CSV",
    image: csvIcon,
  });
  const isDownloadingRef = useRef(false);

  const dispatch = useDispatch();

  const handlePageChange = (page) => {
    if (
      pagination.current + 1 !== page &&
      page <= pagination.totalPages
    ) {
      setPagination((prev) => ({ ...prev, current: page - 1 }));
      onPageChange && onPageChange(page);

      kindOf(getReportData, "function") &&
        getReportData(undefined, undefined, page - 1);
    }
  };
  const handlePerPageChange = (size) => {
    if (pagination.size !== size) {
      const page = 0;
      setPagination((prev) => ({ ...prev, size: size, current: page }));
      onPerPageChange && onPerPageChange(size);
      kindOf(getReportData, "function") &&
        getReportData(undefined, undefined, page, size);
    }
  };
  const downloadContent = () => {
    if (isDownloadingRef.current) {
      showToast("Download already in progress", "warning");
      return;
    }
    if (!pagination.totalPages || pagination.totalPages === 0) {
      showToast("No Data to download", "warning");
      return;
    }

    const totalItems = Number(pagination.totalItems) || 0;
    if (totalItems <= 0) {
      showToast("No Data to download", "warning");
      return;
    }

    if (!getReportThunk) {
      showToast("Download is not available for this report", "warning");
      return;
    }

    let currId = Date.now();
    const controller = new AbortController();
    isDownloadingRef.current = true;

    dispatch(
      addDownloadToLog({
        name: `${reportName}`,
        id: currId,
        totalTime: totalItems * downloadFactor + 400 + 200,
        type: fileType.value,
        url: window.location.href,
        controller: controller,
      })
    );

    dispatch(
      getReportThunk(
        {
          udata: includeTotalCountOnDownload
            ? {
                ...filterData,
                totalCount: totalItems,
              }
            : filterData,
          search: searchVal,
          page: 0,
          size: totalItems,
          download: currId,
          controller: controller,
          ...(includeTotalCountOnDownload
            ? { firstTime: false, totalCount: totalItems }
            : {}),
        },
        { signal: controller.signal }
      )
    )
      .then((action) =>
        middleware(
          action,
          reportName,
          (action) => {
            if (isArray(action.payload?.[contentField])) {
              const raw = selector
                ? selector(action.payload)
                : action.payload?.[contentField];
              const normalizedRows = isArray(raw?.[0]) ? raw[0] : raw;

              let data = mappable(normalizedRows);

              if (!isArray(data) || data.length === 0) {
                showToast("No Data to download", "warning");
                return;
              }

              confirmModalHandler({
                currId,
                data,
                fileType,
                reportName,
                chartsRef,
                mappable,
              });

              if (fileType.value) {
                dispatch(
                  completedDownload({
                    name: `${reportName} ${fileType.value}`,
                    id: currId,
                  })
                );
              }
            } else {
              showToast("Error while Downloading " + reportName, "warning");
            }
          },
          "Error while Downloading " + reportName
        )
      )
      .finally(() => {
        isDownloadingRef.current = false;
      });
  };

  return {
    pagination,
    setPagination,
    fileType,
    setFileType,
    handlePageChange,
    handlePerPageChange,
    downloadContent,
  };
};

export const useDownloadData = (reportData, downloadOptions) => {
  let { reportName, mappable, chartsRef, paginationDefaultSize } =
    downloadOptions ?? {};

  const paginationSize = downloadOptions ? paginationDefaultSize ?? 50 : 10;

  const [pagination, setPagination] = useState({
    current: 0,
    size: paginationSize,
    isLastPage: false,
    totalItems: reportData.length,
    totalPages: Math.ceil(reportData.length / paginationSize),
  });
  const [fileType, setFileType] = useState({
    value: "csv",
    label: "CSV",
    image: csvIcon,
  });

  const dispatch = useDispatch();

  const handlePageChange = (page) => {
    if (
      pagination.current + 1 !== page &&
      reportData.length !== 0 &&
      page <= pagination.totalPages
    ) {
      setPagination((prev) => ({ ...prev, current: page - 1 }));
    }
  };
  const handlePerPageChange = (size) => {
    if (pagination.size !== size) {
      let page = pagination.current;
      if (pagination.current * size > pagination.totalItems)
        page = parseInt(pagination.totalItems / size);

      setPagination((prev) => ({
        ...prev,
        size: size,
        current: page,
        totalPages: Math.ceil(reportData.length / paginationSize),
      }));
    }
  };
  const downloadContent = () => {
    const data = mappable(reportData);
    if (!isArray(data)) {
      showToast("Bad Table Data", "warning");
      return;
    }
    if (data.length === 0) {
      showToast("No Data to download", "warning");
      return;
    }

    confirmModalHandler({
      data,
      fileType,
      reportName,
      chartsRef,
      mappable,
    });
    let dataLength = JSON.stringify(data).length;

    dispatch(
      addSuccessDownloadToLog({
        name: `${reportName}`,
        id: Date.now(),
        totalTime: data.length,
        type: fileType.value,
        url: window.location.href,
        size: dataLength * 1.05,
      })
    );
  };

  useEffect(() => {
    const totalPages = Math.ceil(reportData.length / paginationSize);
    setPagination((prev) => ({
      ...prev,
      totalPages: totalPages,
      current:
        prev.current + 1 > totalPages
          ? Math.max(0, totalPages - 1)
          : prev.current,
      totalItems: reportData.length,
    }));
  }, [reportData.length]);

  return {
    fileType,
    pagination,
    setPagination,
    setFileType,
    downloadContent,
    handlePageChange,
    handlePerPageChange,
  };
};
